import { Link } from "react-router-dom";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Button } from "../ui/button";
import { PATHS, useLang, useT } from "../../lib/i18n";

export const Stat = ({ label, value, accent, testid }) => (
  <div className="surface p-5" data-testid={testid}>
    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
    <p className={`mt-2 font-num text-3xl font-bold ${accent ? "text-[var(--ball)]" : ""}`}>{value}</p>
  </div>
);

const sign = (v) => `${v > 0 ? "+" : ""}${v}`;

export function StatsNote({ gate, className = "" }) {
  const t = useT();
  const min = gate?.min ?? 100;
  const n = Math.min(gate?.settled ?? 0, min);
  return (
    <div className={`surface p-5 text-sm text-muted-foreground ${className}`} data-testid="stats-note">
      <p>{t(`Statistiken werden angezeigt, sobald ${min} Tipps ausgewertet sind.`, `Statistics will be shown once ${min} tips have been settled.`)}</p>
      <div className="mt-3 flex items-center gap-3">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-border"><div className="h-full bg-[var(--ball)]" style={{ width: `${(n / min) * 100}%` }} /></div>
        <span className="font-num text-xs" data-testid="stats-note-progress">{t(`${n} von ${min}`, `${n} of ${min}`)}</span>
      </div>
    </div>
  );
}

export function RecordStats({ total, gate }) {
  const t = useT();
  if (!total) return null;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="record-stats">
      <Stat label="Profit" value={`${sign(total.profit)}u`} accent testid="stat-profit" />
      <Stat label="Yield" value={`${total.yield}%`} testid="stat-yield" />
      <Stat label={t("Trefferquote", "Hit rate")} value={`${total.hit_rate}%`} testid="stat-hitrate" />
      <Stat label={t("Tipps", "Tips")} value={`${total.won}-${total.lost}`} testid="stat-tips" />
    </div>
  );
}

export function RecordChart({ series }) {
  if (!series?.length) return null;
  return (
    <div className="surface h-64 p-4" data-testid="record-chart">
      <ResponsiveContainer>
        <AreaChart data={series}>
          <defs><linearGradient id="g" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#d7ff3a" stopOpacity={0.35} /><stop offset="100%" stopColor="#d7ff3a" stopOpacity={0} /></linearGradient></defs>
          <XAxis dataKey="date" hide />
          <YAxis width={36} tick={{ fill: "#7d8896", fontSize: 11 }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ background: "#0e1217", border: "1px solid #1c232c", borderRadius: 10 }} />
          <Area type="monotone" dataKey="units" stroke="#d7ff3a" strokeWidth={2} fill="url(#g)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function MonthTable({ rows, keyName = "month", label }) {
  const t = useT();
  if (!rows?.length) return null;
  return (
    <div className="surface overflow-x-auto" data-testid={`record-table-${keyName}`}>
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
          <tr>{[label, t("Tipps", "Tips"), "W-L", t("Treffer", "Hit"), "Units", "Yield"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
        </thead>
        <tbody className="font-num">
          {rows.map((r) => (
            <tr key={r[keyName]} className="border-t border-border">
              <td className="px-4 py-3 font-sans font-semibold">{r[keyName]}</td><td className="px-4 py-3">{r.tips}</td><td className="px-4 py-3">{r.won}-{r.lost}</td>
              <td className="px-4 py-3">{r.hit_rate}%</td><td className="px-4 py-3" style={{ color: r.profit >= 0 ? "var(--win)" : "var(--loss)" }}>{sign(r.profit)}</td><td className="px-4 py-3">{r.yield}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Faq({ items }) {
  const t = useT();
  if (!items?.length) return null;
  return (
    <section className="mt-20" data-testid="faq-section">
      <h2 className="font-display text-4xl font-bold">FAQ</h2>
      <p className="mt-1 text-muted-foreground">{t("Häufige Fragen", "Frequently asked questions")}</p>
      <Accordion type="single" collapsible className="mt-6 surface px-5">
        {items.map((f, i) => (
          <AccordionItem key={i} value={`f${i}`} className={i === items.length - 1 ? "border-b-0" : ""}>
            <AccordionTrigger className="text-left text-base" data-testid={`faq-q-${i}`}>{f.q}</AccordionTrigger>
            <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

export function VipCta({ products }) {
  const lang = useLang();
  const t = useT();
  const p = products || {};
  return (
    <section className="relative mt-20 overflow-hidden rounded-3xl border border-[var(--ball)]/30 bg-[radial-gradient(ellipse_at_top_right,rgba(215,255,58,.14),transparent_60%)] p-8 md:p-12" data-testid="vip-cta">
      <div className="grid gap-8 md:grid-cols-2 md:items-center">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ball)]">VIP</p>
          <h2 className="mt-2 font-display text-4xl font-extrabold leading-none md:text-5xl">{t("Alle Tipps. Jeden Tag.", "Every tip. Every day.")}</h2>
          <ul className="mt-6 space-y-2 text-sm">
            {[t("ATP, WTA, Challenger, WTA 125 & ITF", "ATP, WTA, Challenger, WTA 125 & ITF"), t("Per Telegram, E-Mail & optional SMS", "Via Telegram, email & optional SMS"), t("Live-Scores, Quoten & H2H im Konto", "Live scores, odds & H2H in your account"), t("1 Jahr gratis über Partner-Buchmacher", "1 year free via partner bookmaker")].map((x) => (
              <li key={x} className="flex items-center gap-2"><Check size={16} className="text-[var(--ball)]" />{x}</li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col gap-3 md:items-end">
          <p className="font-num text-5xl font-bold">{p.monthly?.price ?? 13}€<span className="text-base text-muted-foreground">/{t("Monat", "month")}</span></p>
          <p className="text-sm text-muted-foreground">{t("Jahrespaket inkl. Grand Slams", "Annual incl. Grand Slams")}: {p.annual?.price ?? 149}€</p>
          <Link to={PATHS[lang].vip}><Button size="lg" className="rounded-full px-8 font-semibold" data-testid="vip-cta-button">{t("VIP werden", "Become VIP")}</Button></Link>
        </div>
      </div>
    </section>
  );
}

export const Section = ({ title, sub, children, testid, action }) => (
  <section className="mt-16" data-testid={testid}>
    <div className="mb-6 flex items-end justify-between gap-4">
      <div><h2 className="font-display text-3xl font-bold md:text-4xl">{title}</h2>{sub && <p className="mt-1 text-sm text-muted-foreground">{sub}</p>}</div>
      {action}
    </div>
    {children}
  </section>
);

export const Crumbs = ({ items }) => (
  <nav aria-label="breadcrumb" className="mb-6 flex flex-wrap gap-1 text-xs text-muted-foreground" data-testid="breadcrumbs">
    {items?.map((c, i) => <span key={c.path}>{i > 0 && " / "}<Link to={c.path} className="hover:text-foreground">{c.name}</Link></span>)}
  </nav>
);
